import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
// componentes
import MapaResultadosBusqueda from "./mapaResultadosBusqueda";
// estilos (fab-cerca-de-mi-*: compartido con botonCercaDeMiBusqueda.jsx)
import "../../estilos/mapaResultados.css";
import { eventoBusquedaRealizada } from "../../funcionesDatos/eventoPropio";
import { pixelBusquedaRealizada } from "../../funcionesDatos/pixelFacebook";
import { gtagBusquedaRealizada } from "../../funcionesDatos/pixelGoogle";
import { ttqBusquedaRealizada } from "../../funcionesDatos/pixelTiktok";

// Recuerda que ya se concedió el permiso y en qué ciudad quedó habilitado, para que en visitas
// siguientes el mapa se abra directo (sin el clic) — pedir la ubicación de nuevo no vuelve a mostrar
// el permiso del navegador (eso ya quedó concedido y es persistente), así que repetirlo es seguro.
// Solo se guarda cuando SÍ quedó habilitado, para no reintentar en automático un caso de "no pudimos
// acceder a tu ubicación" (eso sigue requiriendo un clic explícito, como antes).
const CLAVE_STORAGE = "zervicy_cerca_de_mi";
const FRESCURA_MAX_MS = 1000 * 60 * 60 * 24; // 24h

// Sección "Servicios cerca de ti" en Home. El disparador es un botón flotante circular (fijo a la
// derecha, arriba del botón de WhatsApp) — no un botón grande en medio de la página, y no hay
// dropdown de ciudad (no tiene sentido preguntar la ciudad si la idea es "cerca de MÍ"). Al hacer
// clic pide la ubicación del navegador, el backend resuelve la ciudad más cercana (ver
// backend/routes/cercaDeMi.js -> resolverCiudadPorCoordenadas, sin geocodificación de Google) y
// solo si esa ciudad ya tiene suficientes servicios el mapa aparece en su lugar normal en la
// página (debajo de Categorías populares). Si no, el botón flotante simplemente desaparece.
function SeccionMapaHome({ usuario }) {
  // "boton" | "buscando" | "mapa" | "oculto"
  const [estado, setEstado] = useState("boton");
  const [error, setError] = useState(null);
  const [datosMapa, setDatosMapa] = useState(null);
  // Mismo lat/lng que ya se usó para pedirle al backend los servicios cercanos — se lo pasamos tal
  // cual a MapaResultadosBusqueda para que abra centrado (y las burbujas floten) alrededor de dónde
  // estás de verdad, no del centroide de toda la ciudad (que puede quedar a varios km de ti).
  const [miUbicacion, setMiUbicacion] = useState(null);
  const seccionRef = useRef(null);
  // false cuando el mapa se abrió solo (restaurado desde localStorage) — ahí no hacemos scroll
  // automático apenas carga la página, eso sí se sentiría invasivo. true en cualquier clic real.
  const scrollAlAbrirRef = useRef(true);

  // Al abrir el mapa, lo trae a la vista (el botón flota y puede haberse oprimido desde
  // cualquier punto del scroll de Home) — salvo que se haya restaurado solo al cargar la página.
  useEffect(() => {
    if (estado === "mapa" && seccionRef.current && scrollAlAbrirRef.current) {
      seccionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [estado]);

  // Si ya quedó guardado un "sí funcionó" reciente, reintenta solo al cargar la página — sin scroll.
  useEffect(() => {
    try {
      const guardado = JSON.parse(localStorage.getItem(CLAVE_STORAGE) || "null");
      if (guardado?.ciudad && guardado?.timestamp && (Date.now() - guardado.timestamp) < FRESCURA_MAX_MS) {
        scrollAlAbrirRef.current = false;
        verCercaDeMi();
      }
    } catch { /* localStorage no disponible (modo privado, etc.) — no pasa nada, se pide el clic normal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function verCercaDeMi() {
    if (!navigator.geolocation) {
      setError("Tu navegador no permite compartir tu ubicación.");
      return;
    }

    setError(null);
    setEstado("buscando");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const res = await axios.post("/mapa-servicios-cerca", {
            params: {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              idUsuario: usuario?.idUsuario || null,
            },
          });

          eventoBusquedaRealizada({
            ciudad: res?.data?.ciudad || null,
            idBusqueda: res?.data?.idBusqueda || null,
            cantidadResultados: Array.isArray(res?.data?.servicios) ? res.data.servicios.length : 0,
            usuarioId: usuario?.idUsuario || null,
          });
          pixelBusquedaRealizada({ termino: null, categoria: null });
          gtagBusquedaRealizada({ termino: null, categoria: null, ciudad: res?.data?.ciudad || null });
          ttqBusquedaRealizada({ termino: null, categoria: null });

          if (res?.data?.habilitada) {
            setDatosMapa(res.data);
            setMiUbicacion({ lat: position.coords.latitude, lng: position.coords.longitude });
            setEstado("mapa");
            try {
              localStorage.setItem(CLAVE_STORAGE, JSON.stringify({ ciudad: res.data.ciudad, timestamp: Date.now() }));
            } catch { /* localStorage no disponible — no afecta el resto del flujo */ }
          } else {
            // Sutil: esta ciudad todavía no tiene suficientes servicios, no insistimos con el botón.
            setEstado("oculto");
          }
        } catch (e) {
          setEstado("oculto");
        }
      },
      () => {
        setEstado("boton");
        setError("No pudimos acceder a tu ubicación. Actívala en el navegador e inténtalo de nuevo.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  }

  function alHacerClic() {
    scrollAlAbrirRef.current = true;
    verCercaDeMi();
  }

  if (estado === "oculto") return null;

  return (
    <>
      {estado === "mapa" && datosMapa ? (
        <section className="mt-4" ref={seccionRef}>
          <div className="card border-0 shadow-sm" style={{ borderRadius: 24, overflow: "hidden" }}>
            <div className="card-body p-3 p-md-4">
              <h2 className="h4 fw-bold mb-0">
                Servicios cerca de ti{datosMapa.ciudad ? ` en ${datosMapa.ciudad}` : ""}
              </h2>
              <div className="text-body-secondary mb-3" style={{ fontSize: 14 }}>
                Estos son los proveedores que tienes más cerca.
              </div>

              <MapaResultadosBusqueda
                servicios={datosMapa.servicios}
                serviciosDigitales={datosMapa.serviciosDigitales}
                serviciosDomicilio={datosMapa.serviciosDomicilio}
                centro={datosMapa.centro}
                miUbicacion={miUbicacion}
                habilitado={datosMapa.habilitada}
                cargando={false}
                mensaje={datosMapa.mensaje}
                ciudad={datosMapa.ciudad}
                altura={420}
              />
            </div>
          </div>
        </section>
      ) : (
        <div className="fab-cerca-de-mi-wrapper">
          <span className="fab-cerca-de-mi-etiqueta">
            {error || "Ver servicios cerca de mí"}
          </span>
          <button
            type="button"
            className="fab-cerca-de-mi"
            onClick={alHacerClic}
            disabled={estado === "buscando"}
            title="Ver servicios cerca de mí en el mapa"
            aria-label="Ver servicios cerca de mí en el mapa"
          >
            {estado === "buscando" ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              <i className="bi bi-geo-alt-fill"></i>
            )}
          </button>
        </div>
      )}
    </>
  );
}

export default SeccionMapaHome;
